/**
 * API may return enums as strings via JsonStringEnumConverter.
 * Normalize to the numeric TypeScript enum value for selects and comparisons.
 */
export function normalizeEnumValue<T extends Record<string, string | number>>(
  enumObj: T,
  value: unknown,
  fallback: T[keyof T]
): T[keyof T] {
  const resolved = tryNormalizeEnumValue(enumObj, value);
  return resolved !== undefined ? resolved : fallback;
}

/** Same as normalizeEnumValue but returns undefined when the value cannot be mapped. */
export function tryNormalizeEnumValue<T extends Record<string, string | number>>(
  enumObj: T,
  value: unknown
): T[keyof T] | undefined {
  if (value == null || value === '') return undefined;

  if (typeof value === 'number' && Number.isFinite(value)) {
    const values = Object.values(enumObj).filter(v => typeof v === 'number') as Array<T[keyof T]>;
    if (values.includes(value as T[keyof T])) {
      return value as T[keyof T];
    }
    return undefined;
  }

  if (typeof value === 'string') {
    const byName = (enumObj as Record<string, unknown>)[value];
    if (typeof byName === 'number') {
      return byName as T[keyof T];
    }
    const asNumber = Number(value);
    if (Number.isFinite(asNumber)) {
      const values = Object.values(enumObj).filter(v => typeof v === 'number') as Array<T[keyof T]>;
      if (values.includes(asNumber as T[keyof T])) {
        return asNumber as T[keyof T];
      }
    }
  }

  return undefined;
}

/** Parse a select/form string into a numeric enum, or undefined if empty/invalid. */
export function parseEnumFormValue<T extends Record<string, string | number>>(
  enumObj: T,
  value: string | number | null | undefined
): T[keyof T] | undefined {
  if (value === '' || value == null) return undefined;
  return tryNormalizeEnumValue(enumObj, value);
}

/** Compare API enum (string or number) to a numeric TypeScript enum member. */
export function enumMatches<T extends Record<string, string | number>>(
  enumObj: T,
  value: unknown,
  expected: T[keyof T]
): boolean {
  const normalized = tryNormalizeEnumValue(enumObj, value);
  return normalized !== undefined && normalized === expected;
}
